import { mdiMenu } from "@mdi/js";
import Icon from "@mdi/react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import Button from "components/Button";
import Header from "components/Header";
import Toggle from "components/Toggle";
import { routes } from "constants/routes";
import useDeviceType from "hooks/useDeviceType";
import MainLayout from "layouts/Main";
import { ICardSettingForm, IWalletBalance } from "models/Home";
import { AppDispatch } from "store";
import { updateCardSetting } from "store/home";
import { selectCardSettings } from "store/home/selector";
import styles from "./style.module.scss";
import { updateUserDetails } from "services/api/user";
import { selectFactoringProfile, selectUserDetails } from "store/user/selector";
import { useAnalytics } from "hooks/useAnalytics";

export default function HomeCardSettings() {
  const { isDesktop } = useDeviceType();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const userDetails = useSelector(selectUserDetails);
  const factoringProfile = useSelector(selectFactoringProfile);
  const allowSelfFinanceFr = factoringProfile?.allow_self_finance_fr ?? false;
  const cardSettings = useSelector(selectCardSettings);
  const { trackAllEvents } = useAnalytics();

  const getDataObject = (settings: IWalletBalance[]): ICardSettingForm => {
    return settings.reduce<ICardSettingForm>((acc, { value, show }) => {
      acc[value] = show;
      return acc;
    }, {});
  };

  // Usage
  const dataObject = getDataObject(cardSettings);

  const {
    register,
    formState: { isSubmitting },
    handleSubmit
  } = useForm({
    values: {
      ...dataObject
    }
  });

  const RESERVE_BALANCE = "show_reserve_balance";
  const WALLET_BALANCE = "show_wallet_balance";

  const onSubmit = async (data: ICardSettingForm) => {
    const payload = {
      frontend_settings: data
    };
    await updateUserDetails(userDetails.id, payload);
    dispatch(updateCardSetting(data));
    navigate(routes.HOME);
    trackAllEvents("home_card_settings_save");
  };

  return (
    <MainLayout
      desktopTitle="Home"
      customHeader={<Header label="Card Settings" />}
      className="pb-5 mobile:pb-24">
      <form className={styles.cardSettingsContainer} onSubmit={handleSubmit(onSubmit)}>
        <span>Select and sequence the metrics you want to see on the home page.</span>
        <ul className={styles.wrapper}>
          {cardSettings.map((item) => {
            if (item.value === WALLET_BALANCE && !allowSelfFinanceFr) {
              return null;
            }

            // Removing the reserve balance card entirely for now, and will bring it back later
            // once we create a toggle in admin that will dictate who sees this card and who doesn't
            if (item.value === RESERVE_BALANCE) {
              return null;
            }
            return (
              <li key={item.value} className={styles.listItem}>
                <Icon path={mdiMenu} />
                <span className="flex-1 text-text">{item.title}</span>
                <Toggle name={item.value} register={register} bgColor="before:bg-success" />
              </li>
            );
          })}
        </ul>

        <Button
          type="submit"
          {...(isDesktop && { variant: "outline" })}
          className={styles.saveButton}
          loading={isSubmitting}>
          Save
        </Button>
      </form>
    </MainLayout>
  );
}
